import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { data_from_server_post } from '@/shared/http/client';
import { GlobalContext } from '@/app/providers/globalState';
import { TRAINING_STATUSES, FINAL_TRAINING_STATUSES } from '@/features/trainings/constants';

const useActiveTraining = (usecase) => {
    const {
        trainStatuses,
        registerStartedTraining,
        syncFinalTrainStatuses,
        realtimeResyncTick,
    } = useContext(GlobalContext);
    const [activeTrainFromFetch, setActiveTrainFromFetch] = useState(null);

    const usecaseId = usecase?._id;
    const clientId = usecase?.client_id;

    const startNewTraining = useCallback((distillationEventId) => {
        // Register in the global trainStatuses map (not only in this hook
        // instance): the cross-tab banner is rendered by the task page's own
        // instance, which may live in another component tree or mount only
        // after navigation, and would otherwise wait for the first WebSocket
        // update — minutes away while the training job spins up.
        registerStartedTraining(distillationEventId, usecaseId);
        setActiveTrainFromFetch({
            distillation_event_id: distillationEventId,
            status: TRAINING_STATUSES.WAITING,
            progress: 0,
            error_details: null,
        })
    }, [registerStartedTraining, usecaseId]);

    const clearActiveTraining = useCallback(() => {
        setActiveTrainFromFetch(null);
    }, []);

    const activeTraining = useMemo(() => {
        const knownId = activeTrainFromFetch?.distillation_event_id;

        if (knownId) {
            const specific = trainStatuses[knownId];
            const merged = specific ? { ...activeTrainFromFetch, ...specific } : activeTrainFromFetch;
            const ownerUsecaseId = merged.usecase_id || merged.llm_usecase_id;
            if (ownerUsecaseId && ownerUsecaseId !== usecase?._id) {
                // Stale entry from a different sub-task — fall through to candidate search
            } else {
                return merged;
            }
        }

        const candidates = Object.values(trainStatuses).filter(training =>
            (training.usecase_id === usecase?._id || training.llm_usecase_id === usecase?._id) &&
            !FINAL_TRAINING_STATUSES.includes(training.status)
        );
        if (candidates.length > 0) {
            return candidates.reduce((newest, t) =>
                t.distillation_event_id > newest.distillation_event_id ? t : newest
            );
        }
        return null;
    }, [trainStatuses, usecase, activeTrainFromFetch]);


    // Refetch when the usecase status changes: "Let's Train" flips the status
    // to distillation_in_progress optimistically, and other hook consumers
    // (e.g. the task page banner) discover the new training through this.
    const usecaseStatus = usecase?.status;

    // realtimeResyncTick bumps after a WebSocket gap (reconnect or tab wake):
    // a terminal status update missed during the gap is never re-delivered,
    // so both this fetch and the trainStatuses reconciliation below are
    // needed to take the banner down.
    useEffect(() => {
        if (!clientId) return;
        data_from_server_post(
            "trainings",
            { "value": clientId },
            (result) => {
                const trainings = result.trainings || [];

                // Replay server-confirmed terminal statuses over stale
                // non-final entries in the global map, otherwise the
                // candidate search in the activeTraining memo keeps
                // resurrecting the banner from the stale entry.
                syncFinalTrainStatuses(trainings);

                const activeCandidates = trainings.filter(training =>
                    training.llm_usecase_id === usecaseId &&
                    !FINAL_TRAINING_STATUSES.includes(training.status)
                );

                if (activeCandidates.length > 0) {
                    const newest = activeCandidates.reduce((a, b) =>
                        (b.distillation_event_id || '') > (a.distillation_event_id || '') ? b : a
                    );
                    setActiveTrainFromFetch(newest);
                } else {
                    setActiveTrainFromFetch(prev => {
                        if (!prev) return null;
                        const serverEntry = trainings.find(
                            t => t.distillation_event_id === prev.distillation_event_id
                        );
                        return serverEntry ? null : prev;
                    });
                }
            }
        );
    }, [usecaseId, clientId, usecaseStatus, realtimeResyncTick, syncFinalTrainStatuses]);

    return {
        activeTraining,
        startNewTraining,
        clearActiveTraining,
    };
};


/**
 * Custom hook to manage training progress state and logic
 * @param {Object} usecase - The current usecase object
 * @returns {Object} Training progress state and handlers
 */
export const useTrainingProgress = (usecase) => {
    const [showTrainingProgressCard, setShowTrainingProgressCard] = useState(true);
    const { activeTraining, startNewTraining: activeStartNewTraining, clearActiveTraining } = useActiveTraining(usecase);


    const closeProgressCard = useCallback(() => {
        setShowTrainingProgressCard(false)
    }, []);

    useEffect(() => {
        if (!activeTraining) {
            // reset close button status for next training
            setShowTrainingProgressCard(true);
        }
    }, [activeTraining]);

    const startNewTraining = useCallback((distillationEventId) => {
        // Always show the card when a new training is initiated
        setShowTrainingProgressCard(true);
        activeStartNewTraining(distillationEventId);
    }, [activeStartNewTraining]);
    return {
        showTrainingProgressCard: showTrainingProgressCard && !!activeTraining,
        currentDistillationEventIdOfActiveTraining: activeTraining?.distillation_event_id,
        currentTrainingStatus: activeTraining?.status,
        currentTrainingProgress: activeTraining?.progress || 0,
        currentTrainingErrorDetails: activeTraining?.error_details,
        currentTrainingUsecaseId: activeTraining?.usecase_id || activeTraining?.llm_usecase_id,
        currentTrainingVersion: activeTraining?.version,

        startNewTraining,
        closeProgressCard,
        clearActiveTraining,
    }
}; 
